import { toast } from 'react-toastify';
import { AxiosError, AxiosResponse } from 'axios';

// Utils
import {
  API_KEY_ID_CULTURE,
  API_KEY_NAME,
  AUTHORIZATION_NAME,
  getStorageObject,
  getStorageString,
  getStringStorage,
  logout,
  PREVIOUS_ROUTE,
  saveStringStorage,
  STORAGE_REFRESH_TOKEN,
  STORAGE_TOKEN,
  STORAGE_USER_LOCATION
} from '@services/storage';
import { IUserLocation } from '@/main/hooks/useUserLocation';
import { authApi } from '.';
import { LoginRequest } from '../models/request';
import { HttpStatusEnum } from '../services/enum/HttpStatusEnum';
import { api } from './base/api';
import { RoutesConstants } from '../routes/routesConstants';
import { UserResponse } from '../models/response';
import {
  getAPIAuthorization,
  getAPIPublicAccessKey,
  getCultureBrowser,
  getTranslateByKey,
  isEmpty,
  isNullUndefined,
  isPublicAPI
} from '../services/utils';
import { HttpCodeEnum } from '../services/enum/HttpCodeEnum';
import { BaseResponse, ResultError } from '../models/common';

// Redux
import { store } from '../store';
import { userSetCurrent, UserTypes } from '../store/user';
import { apiIntegration } from './base/apiIntegration';
import { setAccessError } from '../store/accessError';

let isRefreshing: boolean = false;
let failedQueue: any[] = [];

function concatBearerTokenRequest(token: string) {
  return `Bearer ${token}`;
}

export function setTokenToRequest() {
  api.interceptors.request.use(config => {
    if (isPublicAPI(String(config.url))) {
      config.headers[API_KEY_NAME] = getAPIPublicAccessKey();
      config.headers[API_KEY_ID_CULTURE] = getCultureBrowser();
    } else {
      config.headers[AUTHORIZATION_NAME] = getAPIAuthorization();
    }

    const userLocation: IUserLocation | null = getStorageObject(STORAGE_USER_LOCATION);
    config.headers['X-User-Latitude'] = userLocation?.latitude;
    config.headers['X-User-Longitude'] = userLocation?.longitude;
    config.headers['X-User-Accuracy'] = userLocation?.accuracy;

    return config;
  });
}
export function setTokenToRequestIntegration() {
  apiIntegration.interceptors.request.use(config => {
    if (isPublicAPI(String(config.url))) {
      config.headers[API_KEY_NAME] = getAPIPublicAccessKey();
      config.headers[API_KEY_ID_CULTURE] = getCultureBrowser();
    } else {
      config.headers[AUTHORIZATION_NAME] = getAPIAuthorization();
    }

    const userLocation: IUserLocation | null = getStorageObject(STORAGE_USER_LOCATION);
    config.headers['X-User-Latitude'] = userLocation?.latitude;
    config.headers['X-User-Longitude'] = userLocation?.longitude;
    config.headers['X-User-Accuracy'] = userLocation?.accuracy;

    return config;
  });
}

const logoutInterceptor = () => {
  store.dispatch({
    type: UserTypes.USER_ISLOGOUT
  });

  logout();

  const previousRoute =
    getStringStorage(PREVIOUS_ROUTE) || window.location.href;
  if (previousRoute) {
    saveStringStorage(PREVIOUS_ROUTE, previousRoute);
  }

  window.location.href = `#${RoutesConstants.login}`;
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

function handleSucessResponse(response: AxiosResponse<any>) {
  if (
    !response.config?.suppressToast &&
    response.data.errorMessages !== undefined &&
    response.data.errorMessages.length > 0 &&
    response.data.isHideToastNotification !== undefined &&
    !response.data.isHideToastNotification
  ) {
    response.data.errorMessages.forEach((item: any) => {
      toast.error(item.value);
    });
  }
  return response;
}

async function handleErrorResponse(
  err: AxiosError<ResultError | BaseResponse>
) {
  const MESSAGE_ERROR_DEFAULT = getTranslateByKey(
    'information.anUnexpectedErrorOccurredPleaseTryAgainLater',
    true
  );
  const originalRequest = err.config as any;
  const statusCode = Number(err.response?.status);
  const statusTypeErrorCode = err.code;
  const hasTokenInvalid =
    ((err.response?.data as unknown) as string) ===
    HttpCodeEnum.ErrorTokenInvalidUser;
  if (statusCode === HttpStatusEnum.Forbidden) {
    if (hasTokenInvalid) {
      logoutInterceptor();
      return Promise.reject(err);
    }
    store.dispatch(setAccessError(true));
    return Promise.reject(err);
  }
  if (statusTypeErrorCode === HttpCodeEnum.Canceled) {
    const customMessageErrorCanceled =
      'Some request were canceled due to performance';
    return Promise.reject(customMessageErrorCanceled);
  }
 
  if (
    err.code === HttpCodeEnum.TimeOut ||
    statusCode === HttpStatusEnum.InternalServerError
  ) {
    toast.error(MESSAGE_ERROR_DEFAULT);
    return Promise.reject(err);
  }
  if (statusCode === HttpStatusEnum.Unauthorized && !originalRequest._retry) {
    if (isRefreshing) {
      try {
        const token: string = await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        originalRequest.headers.Authorization = concatBearerTokenRequest(token);
        return await api(originalRequest);
      } catch (err_1) {
        return await Promise.reject(err_1);
      }
    }

    originalRequest._retry = true;
    isRefreshing = true;
    if (isEmpty(getStorageString(STORAGE_REFRESH_TOKEN))) {
      logoutInterceptor();
      return Promise.reject(err);
    }

    return new Promise((resolve, reject) => {
      const request: LoginRequest = {
        grant_type: 'refresh_token',
        refresh_token: getStorageString(STORAGE_REFRESH_TOKEN),
        token: getStorageString(STORAGE_TOKEN)
      };

      authApi
        .loginApi(request)
        .then((response: AxiosResponse<any>) => {
          if (response.data && response.data.result) {
            const result = response.data.result as UserResponse;
            saveStringStorage(STORAGE_TOKEN, result.token);
            saveStringStorage(STORAGE_REFRESH_TOKEN, result.refreshToken);
            store.dispatch(
              userSetCurrent({
                ...result,
                pendingAcceptanceTerms: []
              })
            );

            setTokenToRequest();
            const { token } = result;
            originalRequest.headers.Authorization = concatBearerTokenRequest(
              token
            );

            processQueue(null, token);
            resolve(api(originalRequest));
          }
        })
        .catch(() => {
          reject(err);
          logoutInterceptor();
        })
        .finally(() => {
          isRefreshing = false;
        });
    });
  }

  if (statusCode === HttpStatusEnum.BadRequest && err.response) {
    const { data } = err.response as AxiosResponse<BaseResponse>;

    if (originalRequest?.suppressToast) {
      return Promise.reject(err);
    }

    if (
      !isNullUndefined(data.isHideToastNotification) &&
      !data.isHideToastNotification
    ) {
      const isResultError = err.response.data.hasOwnProperty('errors');

      if (isResultError) {
        const responseError = err.response.data as ResultError;
        const messageTitleError = responseError.title;
        const mappedErrors = Object.keys(responseError.errors).map(key => ({
          field: key,
          message: responseError.errors[key] as string[]
        }));
        const messagesDataError = mappedErrors.map(
          item => `${item.field}: ${item.message.map(itemError => itemError)}`
        );
        const messageCodeError = err.message;
        const messageError = `${messageCodeError}\n   ${messageTitleError}\n `;
        return {
          messageError,
          messagesDataError
        };
      } else {
        data.errorMessages?.forEach((item: any) => {
          toast.error(item.value, {
            style: {
              wordBreak: 'break-word'
            }
          });
        });
      }
    }
    if (isNullUndefined(data.isHideToastNotification)) {
      toast.error(MESSAGE_ERROR_DEFAULT);
    }
  }

  return Promise.reject(err);
}

export function handleInterceptors() {
  api.interceptors.response.use(handleSucessResponse, handleErrorResponse);
}

export function handleInterceptorsIntegration() {
  apiIntegration.interceptors.response.use(
    handleSucessResponse,
    handleErrorResponse
  );
}
